/**
 * EarlyAccessProvider — exposes a single hook (`useEarlyAccess`) that any
 * button or link in the app can use to either:
 *   - open the ComingSoonModal (when LK_FEATURES.applicationLive is false), or
 *   - navigate straight into the application (when the flag is true).
 * applicationLive defaults to true unless VITE_FEATURE_APPLICATION_LIVE is set
 * to a falsy value (see src/config/features.js).
 *
 * Mount once at the app root so the modal lives outside per-page state and
 * survives route changes.
 *
 * Usage in any component:
 *   const { startApplication } = useEarlyAccess();
 *   <button onClick={() => startApplication('hero_cta')}>Get Started</button>
 *
 * The optional `source` argument is captured into early_access_signups.source
 * so we can see which CTA drove a signup.
 */
import {
  createContext, useCallback, useContext, useMemo, useState,
} from 'react';
import { useNavigate } from 'react-router';
import ComingSoonModal from './ComingSoonModal';
import { LK_FEATURES } from '../../config/features';

const EarlyAccessContext = createContext({
  isLive: false,
  startApplication: () => {},
  openModal: () => {},
  closeModal: () => {},
});

export const EarlyAccessProvider = ({ children }) => {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [source, setSource] = useState('welcome_page');

  // Default entry point is the Owen readiness experience (/readiness);
  // callers can still pass an explicit path (e.g. the legacy /apply flow).
  const startApplication = useCallback((src = 'welcome_page', applyPath = '/readiness') => {
    if (LK_FEATURES.applicationLive) {
      navigate(applyPath);
    } else {
      setSource(src);
      setOpen(true);
    }
  }, [navigate]);

  const openModal = useCallback((src = 'welcome_page') => {
    setSource(src);
    setOpen(true);
  }, []);

  const closeModal = useCallback(() => setOpen(false), []);

  const value = useMemo(() => ({
    isLive: LK_FEATURES.applicationLive,
    startApplication,
    openModal,
    closeModal,
  }), [startApplication, openModal, closeModal]);

  return (
    <EarlyAccessContext.Provider value={value}>
      {children}
      <ComingSoonModal open={open} onClose={closeModal} source={source} />
    </EarlyAccessContext.Provider>
  );
};

export const useEarlyAccess = () => useContext(EarlyAccessContext);

export default EarlyAccessProvider;
